import { faEye, faEyeSlash } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { CustomSwitch } from "../../../../ui/CustomSwitch";
import { useMapData } from "../../hooks/mapDataAndFilters";

interface PlumeOpacityPanelProps {
    plumeOpacity: number;
    showPlumes: boolean;
    onOpacityChange: (value: number) => void;
    onToggleVisibility: () => void;
    onShowPlumesChange: () => void;
    className?: string;
}

export const PlumeOpacityPanel = ({
    plumeOpacity,
    showPlumes,
    onOpacityChange,
    onToggleVisibility,
    onShowPlumesChange,
    className = "",
}: PlumeOpacityPanelProps) => (
    <div
        className={`flex gap-3 bg-white rounded px-4 py-3 whitespace-nowrap items-center ${className}`}
    >
        Plume opacity
        <button
            className="px-1 rounded hover:bg-gray-200"
            onClick={onToggleVisibility}
            type="button"
        >
            <FontAwesomeIcon
                icon={plumeOpacity ? faEye : faEyeSlash}
                className="w-4"
            />
        </button>
        <input
            type="range"
            className="w-full min-w-20"
            value={plumeOpacity}
            min={0}
            step={0.01}
            max={1}
            onChange={(e) => onOpacityChange(e.target.valueAsNumber)}
        />
        <span className="w-4">{(plumeOpacity * 100).toFixed(0)} %</span>
        <div className="flex w-content items-center gap-3 ml-4">
            <CustomSwitch
                checked={showPlumes}
                onChange={onShowPlumesChange}
                size="xs"
            />
            <div className="w-24">
                {!showPlumes ? "Outlines only" : "Outlines/plumes"}
            </div>
        </div>
    </div>
);

export const PlumeControl = ({
    mapId = "mainMap",
    variant = "lg",
}: {
    mapId?: string;
    variant?: "lg" | "sm";
}) => {
    const {
        mapSettings: { plumeOpacity, showPlumes },
        setMapDataState,
    } = useMapData(mapId);
    const [oldOpacity, setOldOpacity] = useState(0);

    const handleOpacityChange = (value: number) => {
        setMapDataState((ds) => ({
            ...ds,
            mapSettings: {
                ...ds.mapSettings,
                plumeOpacity: value,
            },
        }));
    };

    const handleToggleVisibility = () => {
        if (plumeOpacity === 0) {
            // If opacity and oldOpacity are both 0, then
            // reset to original opacity value.
            if (oldOpacity === 0) {
                setMapDataState((ds) => ({
                    ...ds,
                    mapSettings: {
                        ...ds.mapSettings,
                        plumeOpacity: 0.5,
                    },
                }));
            } else {
                // Else just toggle values normally
                setMapDataState((ds) => ({
                    ...ds,
                    mapSettings: {
                        ...ds.mapSettings,
                        plumeOpacity: oldOpacity,
                    },
                }));
                setOldOpacity(0);
            }
        } else {
            setMapDataState((ds) => ({
                ...ds,
                mapSettings: {
                    ...ds.mapSettings,
                    plumeOpacity: 0,
                },
            }));
            setOldOpacity(plumeOpacity);
        }
    };

    const handleShowPlumesChange = () => {
        setMapDataState((ds) => ({
            ...ds,
            mapSettings: {
                ...ds.mapSettings,
                showPlumes: !showPlumes,
            },
        }));
    };

    return (
        <PlumeOpacityPanel
            className={variant === "lg" ? "h-8 text-sm" : "h-7 text-sm"}
            plumeOpacity={plumeOpacity}
            showPlumes={showPlumes}
            onOpacityChange={handleOpacityChange}
            onToggleVisibility={handleToggleVisibility}
            onShowPlumesChange={handleShowPlumesChange}
        />
    );
};
